// The React Compiler mis-compiles this factory: its inner `use*` hooks close over the factory's
// mutable `updaters` object, and the compiler outlines the `.map`/`.forEach` callbacks to module
// scope, where `updaters` is not defined — a `ReferenceError` at runtime. Opt the whole file out
// of the compiler (memoizing these thin wrappers adds no value anyway). See `"use no memo"`.
'use no memo';
import { useMutation } from '@apollo/client/react';
import React from 'react';
import { REST_GRAPHQL_CLIENTNAME } from '@famly/stat_apollo-client';
import { isDiscriminatedProps, toDiscriminatedProps } from '@famly/vet_apollo-utils';
import { updateApolloCache } from '@famly/vet_apollo-utils_update-apollo-cache';
const updateIdGenerator = (function* getId() {
    let id = 0;
    while (true) {
        yield id++;
    }
})();
/**
 * Create a mutation callback that other components can "subscribe" to to update their part
 * of the cache.
 *
 * @example
 * const {useSubscribableMutation, useUpdateCache} = createSubscribableMutator<CreateFooMutation, CreateFooMutationVariables>(CreateFoo);
 *
 * MutatorComponent() {
 *  const [mutate, {loading}] = useSubscribableMutation();
 *  return (
 *    <button onClick={mutate} />
 *  )
 * }
 *
 * OtherComponent() {
 *   useUpdateCache<ListFooQuery, ListFooQueryVariables>({
 *     cacheQuery: ListFoo,
 *     cacheVariables: {...},
 *     updater: (cachedData, result) => [
 *       result,
 *       ...cachedData,
 *     ]
 *   })
 *  ...
 * }
 */
export const createSubscribableMutator = (mutation, subscribableMutatorOptions) => {
    // Apollo 3.8+ types `MutationUpdaterFunction` with `Unmasked<TData>` to support data masking.
    // We don't use masked fragments, so the runtime shape is identical to `TData`.
    const updaters = {};
    const resultSubscriptions = [];
    const errorSubscriptions = [];
    const useSubscribableMutation = (options) => {
        var _a;
        return ((_a = subscribableMutatorOptions === null || subscribableMutatorOptions === void 0 ? void 0 : subscribableMutatorOptions.useCustomMutationHook) !== null && _a !== void 0 ? _a : (useMutation))(mutation, {
            onError: error => {
                // By default we only log a warning, since Apollo takes care of displaying & logging errors via our ErrorLink
                console.warn('Error when calling mutation', error);
                errorSubscriptions.forEach(subscription => subscription(error, options));
            },
            ...(options || {}),
            context: {
                ...(options || {}),
                ...((options === null || options === void 0 ? void 0 : options.context) || {}),
                clientName: (subscribableMutatorOptions === null || subscribableMutatorOptions === void 0 ? void 0 : subscribableMutatorOptions.withRest) ? REST_GRAPHQL_CLIENTNAME : undefined,
            },
            update: (proxy, result, callOptions) => {
                if (options && options.update) {
                    options.update(proxy, result, {});
                }
                const typedResult = result;
                Object.values(updaters).forEach(updater => updater(proxy, result, callOptions));
                resultSubscriptions.forEach(subscription => subscription(typedResult.data, callOptions));
            },
        });
    };
    const useUpdateCache = (...args) => {
        React.useEffect(() => {
            const updateTuples = args.map(arg => {
                /**
                 * For some reason Typescript cannot handle the union type in `UpdateApolloCache`
                 * but we can convert the union to the type DiscriminatedProps without any downsides
                 * and have everything compile safely.
                 */
                const discriminatedProps = isDiscriminatedProps(arg) ? arg : toDiscriminatedProps(arg);
                return [
                    updateIdGenerator.next().value.toString(),
                    updateApolloCache()(discriminatedProps),
                ];
            });
            updateTuples.forEach(([id, updater]) => {
                updaters[id] = updater;
            });
            return () => {
                updateTuples.forEach(([id]) => {
                    delete updaters[id];
                });
            };
        }, [args]);
    };
    const useSubscribeMutationResult = (cb) => {
        const cancelCallback = React.useCallback(() => {
            // eslint-disable-next-line local-rules/no-mutable-array-methods -- pre-existing mutating array call flagged when local-rules/no-mutable-array-methods was added; migrate to the immutable equivalent (toSorted/toReversed/toSpliced) separately
            resultSubscriptions.splice(resultSubscriptions.findIndex(subscription => subscription === cb), 1);
        }, [cb]);
        React.useEffect(() => {
            resultSubscriptions.push(cb);
            return cancelCallback;
        }, [cancelCallback, cb]);
    };
    const useSubscribeMutationError = (cb) => {
        const cancelCallback = React.useCallback(() => {
            // eslint-disable-next-line local-rules/no-mutable-array-methods -- pre-existing mutating array call flagged when local-rules/no-mutable-array-methods was added; migrate to the immutable equivalent (toSorted/toReversed/toSpliced) separately
            errorSubscriptions.splice(errorSubscriptions.findIndex(subscription => subscription === cb), 1);
        }, [cb]);
        React.useEffect(() => {
            errorSubscriptions.push(cb);
            return cancelCallback;
        }, [cancelCallback, cb]);
    };
    return { useSubscribableMutation, useUpdateCache, useSubscribeMutationResult, useSubscribeMutationError };
};
